import { z } from "zod";
import { appIdSchema, isoTimestampSchema, userIdSchema, } from "./identifiers.js";
import { aiNanodollarsSchema } from "./platform-ai.js";
import { providerCredentialStateSchema } from "./providers.js";
const boundedIdentifierSchema = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z0-9._:-]+$/);
const tokenCountSchema = z.number().int().nonnegative().nullable();
export const aiInferenceEndpointSchema = z.enum([
    "chat.completions",
    "images.generations",
]);
/**
 * `openrouter` runs in the isolated OpenRouter worker with the provider's
 * native model id (`vendor/model`), which contains a slash.
 */
export const aiInferenceProviderSchema = z.enum([
    "openai",
    "anthropic",
    "openrouter",
]);
export const PLATFORM_THEME_SERVICE_APP_ID = "7b945d92-b48c-4f3d-9e18-bab43fc075c7";
export const PLATFORM_THEME_SERVICE_DEPLOYMENT_ID = "58f0e9af-9e17-4a0f-b14c-b39bf8ca71c0";
export const PLATFORM_THEME_SERVICE_FUNCTION_NAME = "theme-import";
export const PLATFORM_AGENT_TITLE_SERVICE_APP_ID = "d5aa91d6-ce52-4eb6-9ef3-a458d868dc32";
export const PLATFORM_AGENT_TITLE_SERVICE_DEPLOYMENT_ID = "c13d40fb-677a-4f67-b7ed-7248b98aee99";
export const PLATFORM_AGENT_TITLE_SERVICE_FUNCTION_NAME = "agent-title";
/** Invocation-scoped authority minted by the trusted Function supervisor. */
export const functionAiGrantClaimsSchema = z
    .object({
    schemaVersion: z.literal(1),
    issuer: z.literal("opencloud-functions"),
    audience: z.literal("opencloud-ai-gateway"),
    tokenId: z.uuid(),
    appId: appIdSchema,
    deploymentId: z.uuid(),
    functionName: z.string().min(1).max(63).regex(/^[a-z][a-z0-9-]*$/),
    requestId: boundedIdentifierSchema,
    correlationId: boundedIdentifierSchema,
    invocation: z.enum(["http", "cron", "job", "explicit-dev"]),
    executionLineageId: z.uuid().optional(),
    principal: z.enum(["user", "system", "anonymous"]).optional(),
    cronInvocationId: z.uuid().nullable(),
    userId: userIdSchema.nullable().default(null),
    environment: z.enum(["production", "dev"]),
    issuedAt: z.number().int().nonnegative(),
    expiresAt: z.number().int().positive(),
})
    .strict()
    .refine((value) => value.expiresAt > value.issuedAt, {
    message: "AI grant expiry must follow issuance",
});
/** Short-lived authority for an internal platform service using AI Capacity. */
export const platformServiceAiGrantClaimsSchema = z
    .object({
    schemaVersion: z.literal(1),
    issuer: z.literal("opencloud-platform-services"),
    audience: z.literal("opencloud-ai-gateway"),
    tokenId: z.uuid(),
    service: z.enum(["theme", "agent-title"]),
    organisationId: z.uuid().optional(),
    requestId: boundedIdentifierSchema,
    correlationId: boundedIdentifierSchema,
    userId: userIdSchema,
    issuedAt: z.number().int().nonnegative(),
    expiresAt: z.number().int().positive(),
})
    .strict()
    .refine((value) => value.expiresAt > value.issuedAt, {
    message: "AI grant expiry must follow issuance",
});
export const aiGatewayGrantClaimsSchema = z.union([
    functionAiGrantClaimsSchema,
    platformServiceAiGrantClaimsSchema,
]);
/** Verified signed Function coordinates forwarded only by the trusted gateway. */
export const functionAiAuthoritySchema = z.object({ kind: z.literal("function"), callId: z.uuid(),
    grant: functionAiGrantClaimsSchema.safeExtend({ executionLineageId: z.uuid(), principal: z.enum(["user", "system", "anonymous"]) }) }).strict();
/** Sensitive launch material. It is restricted to agent-service -> launcher. */
export const codexInferenceLaunchRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    callId: z.uuid(),
    userId: userIdSchema,
    appId: appIdSchema,
    providerConnectionId: z.uuid(),
    providerStateVersion: z.number().int().positive(),
    provider: aiInferenceProviderSchema,
    model: z.string().trim().min(1).max(200),
    endpoint: aiInferenceEndpointSchema,
    authJson: z.string().min(2).max(500_000),
    controlToken: z.string().min(32).max(4_096),
    refreshCredential: z.boolean(),
    executionClass: z
        .enum(["user_execution", "credential_maintenance"])
        .optional(),
    providerStateCallbackUrl: z.url(),
    providerStateCallbackToken: z.string().min(32).max(4_096),
    expiresAt: isoTimestampSchema,
    /**
     * OpenRouter only: route the call only to providers that neither train on
     * nor retain prompts (`provider.data_collection: "deny"`).
     */
    privateProvidersOnly: z.boolean().optional(),
})
    .strict();
export const codexInferenceRuntimeStateSchema = z.enum([
    "starting",
    "running",
    "completed",
    "failed",
    "cancelled",
    "removed",
]);
export const codexInferenceHandleSchema = z
    .object({
    schemaVersion: z.literal(1),
    callId: z.uuid(),
    userId: userIdSchema,
    appId: appIdSchema,
    providerConnectionId: z.uuid(),
    providerStateVersion: z.number().int().positive(),
    provider: aiInferenceProviderSchema,
    model: z.string().trim().min(1).max(200),
    runtimeId: z.string().min(1).max(200),
    runtimeUrl: z.url(),
    containerName: z.string().min(1).max(128),
    state: codexInferenceRuntimeStateSchema,
    expiresAt: isoTimestampSchema,
    errorClass: z.string().trim().min(1).max(200).nullable(),
    retryable: z.boolean(),
})
    .strict();
export const codexInferenceControlRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    reason: z.string().trim().min(1).max(500),
})
    .strict();
export const aiCallUsageSchema = z
    .object({
    inputTokens: tokenCountSchema,
    cachedInputTokens: tokenCountSchema,
    cacheWriteTokens: tokenCountSchema.optional(),
    outputTokens: tokenCountSchema,
    reasoningTokens: tokenCountSchema,
    totalTokens: tokenCountSchema,
    /**
     * USD nanodollars the provider itself reported for this call (OpenRouter
     * `usage.cost`). Absent or null when the provider reports no cost; settle
     * it as `providerReportedCostNanodollars`.
     */
    costNanodollars: aiNanodollarsSchema.nullable().optional(),
})
    .strict();
export const aiInferencePrepareRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    callId: z.uuid(),
    appId: appIdSchema,
    deploymentId: z.uuid(),
    functionName: z.string().min(1).max(63).regex(/^[a-z][a-z0-9-]*$/),
    functionRequestId: boundedIdentifierSchema,
    correlationId: boundedIdentifierSchema,
    invocation: z.enum(["http", "cron", "job", "explicit-dev"]),
    cronInvocationId: z.uuid().nullable(),
    userId: userIdSchema.nullable().default(null),
    endpoint: aiInferenceEndpointSchema,
    model: z.string().trim().min(1).max(200),
    stream: z.boolean(),
    parameters: z.record(z.string(), z.unknown()),
    requestDigest: z.string().regex(/^[a-f0-9]{64}$/),
    expiresAt: isoTimestampSchema,
    platformService: z.enum(["theme", "agent-title"]).optional(),
    organisationId: z.uuid().optional(),
    functionAuthority: functionAiAuthoritySchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.platformService && !value.userId) {
        context.addIssue({
            code: "custom",
            path: ["userId"],
            message: "Platform-service inference requires an attributed user",
        });
    }
    if (value.platformService && value.endpoint !== "chat.completions") {
        context.addIssue({
            code: "custom",
            path: ["endpoint"],
            message: "Platform-service inference supports chat completions only",
        });
    }
});
export const aiInferenceUserPrepareResponseSchema = z
    .object({
    schemaVersion: z.literal(1),
    callId: z.uuid(),
    userId: userIdSchema,
    appId: appIdSchema,
    providerConnectionId: z.uuid(),
    providerStateVersion: z.number().int().positive(),
    provider: aiInferenceProviderSchema,
    model: z.string().trim().min(1).max(200),
    reasoningEffort: z.enum([
        "none",
        "minimal",
        "low",
        "medium",
        "high",
        "xhigh",
        "max",
    ]),
    runtimeUrl: z.url(),
    runtimeToken: z.string().min(32).max(4_096),
    expiresAt: isoTimestampSchema,
})
    .strict();
export const aiInferencePlatformPrepareResponseSchema = z
    .object({
    schemaVersion: z.literal(2),
    fundingSource: z.literal("platform"),
    callId: z.uuid(),
    userId: userIdSchema,
    appId: appIdSchema,
    reservationId: z.uuid(),
    provider: z.enum(["openai", "anthropic"]),
    model: z.string().trim().min(1).max(200),
    reasoningEffort: z.enum([
        "none",
        "minimal",
        "low",
        "medium",
        "high",
        "xhigh",
        "max",
    ]),
    runtimeUrl: z.url(),
    runtimeToken: z.string().min(32).max(4_096),
    expiresAt: isoTimestampSchema,
})
    .strict();
export const aiInferencePrepareResponseSchema = z.discriminatedUnion("schemaVersion", [aiInferenceUserPrepareResponseSchema, aiInferencePlatformPrepareResponseSchema]);
/** Sensitive, call-authenticated credential rotation from a Codex worker. */
export const aiInferenceProviderStateUpdateSchema = z
    .object({
    schemaVersion: z.literal(1),
    callId: z.uuid(),
    userId: userIdSchema,
    appId: appIdSchema,
    providerConnectionId: z.uuid(),
    expectedStateVersion: z.number().int().positive(),
    state: providerCredentialStateSchema,
})
    .strict();
export const aiInferenceSettleRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    callId: z.uuid(),
    userId: userIdSchema,
    outcome: z.enum(["completed", "failed", "cancelled", "timed_out"]),
    usage: aiCallUsageSchema,
    responseBytes: z.number().int().nonnegative(),
    errorClass: z.string().trim().min(1).max(200).nullable(),
    upstreamStatus: z.number().int().min(100).max(599).nullable(),
})
    .strict();
//# sourceMappingURL=inference.js.map